import styled, { css, keyframes } from 'styled-components'
import { appTypeConfig } from '../../common/config'
import Box from '../components/Box'
import BulkOrderScrollContainer from './BulkOrderScrollContainer'
import { SCROLL_FADE_HEIGHT_GRID } from './bulkOrderScrollFade.styles'

export const CONTROL_PULSE_DURATION_S = 0.55
export const CONTROL_PULSE_ITERATIONS = 1
export const CONTROL_PULSE_DURATION_MS =
  CONTROL_PULSE_DURATION_S * CONTROL_PULSE_ITERATIONS * 1000

export const Panel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  width: 100%;
  min-width: 0;
  overflow: hidden;
`

// Matches SidebarHeader padding + TextField input height (1.2rem + 3rem + 1.2rem)
const panelTitleBarHeight = '5.4rem'

export const PanelHeader = styled.div`
  position: relative;
  z-index: 5;
  flex-shrink: 0;
  padding: 0 4rem 0 2rem;
`

export const ProductTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.6rem;
  min-height: ${panelTitleBarHeight};
  padding: 1.2rem 0;
  margin-bottom: 0.2rem;
`

export const FullWidthToggle = styled.div`
  margin-left: auto;
  flex-shrink: 0;
`

export const ProductTitle = styled(Box.Title)`
  ${({ theme }) => css`
    margin: 0;
    min-width: 0;

    ${!appTypeConfig?.colors?.titleColor &&
    css`
      color: ${theme.colors.boxTitle};
    `}
  `}
`

export const ControlsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.6rem 2.4rem;
`

export const ControlField = styled.div`
  min-width: 12rem;
  flex: 0 0 14rem;
`

const controlBorderPulseKeyframes = theme => keyframes`
  0% {
    border-color: ${theme.field.borderColor};
    box-shadow: 0 0 0 0 transparent;
  }
  50% {
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 4px ${theme.colors.primaryVeryLight};
  }
  100% {
    border-color: ${theme.field.borderColor};
    box-shadow: 0 0 0 0 transparent;
  }
`

/** Soft glow around a whole switch pill (no per-button border flash). */
const switchControlPulseKeyframes = theme => keyframes`
  0% {
    box-shadow: 0 0 0 0 transparent;
  }
  50% {
    box-shadow: 0 0 0 4px ${theme.colors.primaryVeryLight};
  }
  100% {
    box-shadow: 0 0 0 0 transparent;
  }
`

/** Pulses react-select control via stable classNamePrefix (BQ-008). */
export const PulsingControlField = styled(ControlField)`
  ${({ $pulse, theme }) =>
    $pulse &&
    css`
      & .bulkOrderSelect__control {
        @media (prefers-reduced-motion: no-preference) {
          animation: ${controlBorderPulseKeyframes(theme)} ${CONTROL_PULSE_DURATION_S}s ease-in-out
            ${CONTROL_PULSE_ITERATIONS};
          animation-fill-mode: forwards;
        }
      }
    `}
`

/** Shrink-to-fit wrapper for SwitchField; pulses the whole pill on recap focus. */
export const PulsingSwitchControl = styled.div`
  flex: 0 0 auto;

  ${({ $pulse, theme }) =>
    $pulse &&
    css`
      /* Switch flex row only (Label is also a div — exclude it) */
      & > div > div:has(> button) {
        border-radius: 25px;
        @media (prefers-reduced-motion: no-preference) {
          animation: ${switchControlPulseKeyframes(theme)} ${CONTROL_PULSE_DURATION_S}s ease-in-out
            ${CONTROL_PULSE_ITERATIONS};
          animation-fill-mode: forwards;
        }
      }
    `}
`

export const PanelBody = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`

export const LeftPane = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`

export const RecapPane = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 20rem;
  min-height: 0;
  padding: 1.6rem 0 2rem 1.2rem;
  border-left: 1px solid ${({ theme }) => theme.colors.gray3};
  overflow: hidden;
`

export const RecapScrollArea = styled(BulkOrderScrollContainer)`
  flex: 1;
  min-height: 0;
`

export const RecapFooter = styled.div`
  flex-shrink: 0;
  padding-top: 2.4rem;
  padding-right: 1.2rem;

  a {
    display: block;
    width: 100%;
  }

  button {
    width: 100%;
  }
`

export const GridSection = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  padding: 2.4rem 4rem 2rem 2rem;
  overflow: hidden;
`

export const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 24rem;
  padding: 2rem;
  text-align: center;
  font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.text3};
`

export const EmptyStateContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3.2rem;
`

export const EmptyStateText = styled.p`
  margin: 0;
`

export const GridScrollArea = styled(BulkOrderScrollContainer).attrs({
  showScrollFade: true,
  scrollFadeVariant: 'light',
  scrollFadeHeight: SCROLL_FADE_HEIGHT_GRID,
  reserveScrollbarGutter: true,
})`
  flex: 1;
  min-height: 0;
`
